import { useGetPromptLibrary } from "@queries/chat/useGetPromptLibrary";
import type { PromptLibraryItem } from "@shared/apis/sidekick/generated";
import { useAuthStore } from "@store/auth.store";
import {
  PromptLibraryPanel,
  PromptLibraryPanelSkeleton,
} from "./PromptLibraryPanel";
import { EmailSignalsSection } from "./EmailSignalsSection";
import { RadarSignalsPanel } from "./RadarSignalsPanel";
import {
  FocusColumns,
  FocusRoot,
  PriorityCard,
  RadarPanelRow,
  SecondaryColumnSlot,
  WelcomeBlock,
  WelcomeTitle,
} from "./ChatFocusView.styles";
import type { FocusLeadSignal } from "./focusLeadSignal";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";

type Props = {
  isSendingMessage: boolean;
  onPromptItemClick: (item: PromptLibraryItem) => void;
  onLeadClick: (lead: FocusLeadSignal) => void;
};

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export const ChatFocusView = ({
  isSendingMessage,
  onPromptItemClick,
  onLeadClick,
}: Props) => {
  const firstName = useAuthStore((s) => s.user.firstName);
  const createdOn = useAuthStore((s) => s.user.createdOn);
  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const { isLoading, data } = useGetPromptLibrary({
    enabled: !hasRadarModule,
  });

  /**
   * There's no lastLoginAt/lastSeenAt on the user - createdOn is the only
   * account-lifecycle timestamp we have, so it's used as a "returning user"
   * proxy: past their first 24h, greet as a returning user.
   */
  const isReturningUser =
    Date.now() - new Date(createdOn).getTime() > ONE_DAY_IN_MS;

  return (
    <FocusRoot>
      <WelcomeBlock>
        <WelcomeTitle variant="h4">
          Welcome{isReturningUser ? " back" : ""}, {firstName}!
        </WelcomeTitle>
      </WelcomeBlock>

      {hasRadarModule ? (
        <RadarPanelRow>
          <RadarSignalsPanel />
        </RadarPanelRow>
      ) : (
        <FocusColumns>
          <PriorityCard aria-labelledby="focus-priority-heading">
            <EmailSignalsSection
              title="Who to Reach Out To"
              subtitle="Contacts with fresh signals, your best starting points for today."
              onLeadClick={onLeadClick}
              disabled={isSendingMessage}
            />
          </PriorityCard>

          <SecondaryColumnSlot>
            {isLoading || !data?.categories ? (
              <PromptLibraryPanelSkeleton />
            ) : (
              <PromptLibraryPanel
                categories={data.categories}
                disabled={isSendingMessage}
                onItemClick={onPromptItemClick}
              />
            )}
          </SecondaryColumnSlot>
        </FocusColumns>
      )}
    </FocusRoot>
  );
};
